<template>
  <div>
    <AlertLayout title="Reminders!"
      message="Kindly observe the facility's turn around time policy on received blood requisitions.<br>Click on Details button for more actions and information on the requisition of choice."
      type="warning" />
    <div class="flex flex-wrap items-center justify-between py-1 pb-4">
      <div class="flex items-center space-x-4">
        <div class="relative" v-if="currentTab === 'blood-units' || currentTab === 'issued-units' || currentTab === 'external-issuance'">
          <button id="dropdownRadioButton"
            class="inline-flex items-center px-6 py-2 text-gray-400 bg-white border border-gray-300 rounded-md"
            type="button" @click="toggleDropdown">
            <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
              fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z" />
            </svg>
            Filters
            <svg class="w-2.5 h-2.5 ms-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 10 6">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="m1 1 4 4 4-4" />
            </svg>
          </button>

          <!-- Filter Dropdown -->
          <template v-if="currentTab === 'blood-units' || currentTab === 'issued-units' || currentTab === 'external-issuance'">
            <div v-show="isDropdownOpen"
              class="absolute z-10 p-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg w-80">
              <div class="flex">
                <button type="button"
                  class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
                  @click="closeDropdown" aria-label="Close">
                  <span class="sr-only">Dismiss</span>
                  <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none"
                    viewBox="0 0 14 14">
                    <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                  </svg>
                </button>
              </div>
              <form class="max-w-sm mx-auto" @submit.prevent="applyFilters">
                <!-- Filters for all tabs -->
                <div class="mb-4">
                  <label for="bloodGroup" class="block mb-2 text-sm font-medium text-gray-900">Blood Group</label>
                  <select v-model="filters.blood_group"
                    class="block w-full px-1 py-2 text-gray-900 border border-gray-200 rounded-md bg-gray-50 dark:placeholder-gray-400">
                    <option value="" disabled selected>Select blood group</option>
                    <option v-for="group in bloodGroups" :key="group.blood_group_id" :value="group.description">
                      {{ group.description }}
                    </option>
                  </select>
                </div>
                <div class="mb-4">
                  <label for="unitProduct" class="block mb-2 text-sm font-medium text-gray-900">Unit Product</label>
                  <select v-model="filters.unit_product"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                    <option value="" disabled selected>Select unit product</option>
                    <option v-for="product in bloodProducts" :key="product.unit_product_id" :value="product.name">
                      {{ product.name }}
                    </option>
                  </select>
                </div>
                <div v-if="currentTab === 'blood-units'" class="mb-4">
                  <label for="statusFilter" class="block mb-2 text-sm font-medium text-gray-900">Status</label>
                  <select id="statusFilter" v-model="filters.status"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                    <option value="" disabled selected>Select</option>
                    <option value="Okay">Okay</option>
                    <option value="Assigned">Assigned</option>
                    <option value="Issued">Issued</option>
                    <option value="Transfused">Transfused</option>
                    <option value="Expired">Expired</option>
                    <option value="Segregated">Segregated</option>
                  </select>
                </div>
                <div v-if="currentTab === 'issued-units'" class="mb-4">
                  <label for="statusFilter" class="block mb-2 text-sm font-medium text-gray-900">Status</label>
                  <select id="statusFilter" v-model="filters.status"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                    <option value="" disabled selected>Select</option>
                    <option value="Issued">Received</option>
                    <option value="Transfused">Transfused</option>
                  </select>
                </div>
                <div v-if="currentTab === 'external-issuance'" class="mb-4">
                  <label for="statusFilter" class="block mb-2 text-sm font-medium text-gray-900">Status</label>
                  <select id="statusFilter" v-model="filters.status"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                    <option value="" disabled selected>Select</option>
                    <option value="Transferred">Transferred</option>
                    <option value="Received">Received</option>
                  </select>
                </div>
                <div class="mb-4">
                  <label for="dateFrom" class="block mb-2 text-sm font-medium text-gray-900">Date From</label>
                  <input type="date" v-model="filters.start_date" id="dateFrom"
                    class="w-full px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
                    placeholder="Date Range" />
                </div>
                <div class="mb-4">
                  <label for="dateTo" class="block mb-2 text-sm font-medium text-gray-900">Date To</label>
                  <input type="date" v-model="filters.end_date" id="dateTo"
                    class="w-full px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
                    placeholder="Date Range" />
                </div>
                <div class="flex justify-end space-x-2">
                  <button type="button" @click="cancelFilters"
                    class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">
                    Cancel
                  </button>
                  <button type="submit"
                    class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-custom-yellow hover:bg-custom-yellow-dark">
                    Filter
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>

        <div class="relative flex" v-if="currentTab === 'blood-units' || currentTab === 'issued-units'">
          <input type="text" v-model="searchQuery" id="table-search"
            class="px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
            placeholder="Search" />
          <button type="submit" class="px-2 py-0 text-white rounded-r bg-custom-yellow md:px-3 md:py-1">
            <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
              width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
            </svg>
          </button>
        </div>

        <div class="relative flex" v-if="currentTab === 'external-issuance'">
          <input type="text" v-model="searchQuery" id="table-search"
            class="px-4 py-2 text-gray-800 border border-gray-300 rounded-sm dark:border-gray-300 dark:placeholder-gray-400"
            placeholder="Search" />
          <button type="submit" class="px-2 py-0 text-white rounded-r bg-custom-yellow md:px-3 md:py-1">
            <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
              width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
            </svg>
          </button>
        </div>
      </div>
      <div class="flex items-center mt-4 space-x-4 sm:mt-0">
        <template v-if="currentTab === 'blood-units'">
          <CustomButton label="Download" bg-class="bg-custom-yellow" @click="handleDownloadBloodUnits" />
        </template>
        <template v-if="currentTab === 'issued-units'">
          <CustomButton label="Download" bg-class="bg-custom-yellow" @click="handleDownloadIssuedUnits" />
        </template>
        <template v-if="currentTab === 'external-issuance'">
          <CustomButton label="Download" bg-class="bg-custom-yellow" @click="handleDownloadExternalIssuedUnits" />
        </template>
      </div>
      <div v-if="isPageLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div class="w-12 h-12 border-t-4 border-b-4 border-yellow-500 rounded-full animate-spin"></div>
      </div>
    </div>
    <div class="flex mb-4 border-b tabs">
      <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'dashboard' }" @click="currentTab = 'dashboard'">
        Dashboard
      </button>
      <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'blood-units' }" @click="currentTab = 'blood-units'">
        Blood Units
      </button>
      <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'issued-units' }" @click="currentTab = 'issued-units'">
        Issued Units
      </button>
      <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
        :class="{ 'border-custom-yellow': currentTab === 'external-issuance' }" @click="currentTab = 'external-issuance'">
        External Issuance
      </button>
    </div>
    <component :is="currentTabComponent" ref="currentTabComponent" :search-query="searchQuery" :date-range="dateRange" :filters="filters" @cancel-filters="cancelFilters"/>
  </div>
</template>

<script>
import AlertLayout from "@/components/AlertLayout.vue";
import CustomButton from "@/components/CustomButton.vue";
import BloodUnitsReportsTab from "../tabs/BloodUnitsReportsTab.vue";
import DemandReportTab from "../tabs/DemandReportTab.vue";
import IssuedUnitsReportsTab from "../tabs/IssuedUnitsReportsTab.vue";
import ExternalUnitIssuanceReportsTab from "../tabs/ExternalUnitIssuanceReportsTab.vue";
import api from '@/services/api';

export default {
  name: "InventoryReportsPage",
  components: {
    AlertLayout,
    CustomButton,
    BloodUnitsReportsTab,
    DemandReportTab,
    IssuedUnitsReportsTab,
    ExternalUnitIssuanceReportsTab
  },
  data() {
    return {
      searchQuery: "",
      dateRange: "",
      isDropdownOpen: false,
      currentTab: "dashboard",
      user: [],
      filters: {
        blood_group: "",
        status: "",
        unit_product: "",
        start_date: "",
        end_date: ""
      },
      bloodGroups: [],
      bloodProducts: []
    };
  },
  created() {
    // Restore the currentTab from localStorage if it exists
    const savedTab = localStorage.getItem('inventoryReportsPageTab');
    if (savedTab && [
      'dashboard',
      'blood-units',
      'issued-units',
      'external-issuance'
    ].includes(savedTab)) {
      this.currentTab = savedTab;
    }
    this.fetchBloodGroups();
    this.fetchBloodProducts();
  },
  watch: {
    // Watch for changes to currentTab and save to localStorage
    currentTab(newTab) {
      localStorage.setItem('inventoryReportsPageTab', newTab);
    }
  },
  computed: {
    currentTabComponent() {
      switch (this.currentTab) {
        case "dashboard":
          return "DemandReportTab";
        case "blood-units":
          return "BloodUnitsReportsTab";
        case "issued-units":
          return "IssuedUnitsReportsTab";
        case "external-issuance":
          return "ExternalUnitIssuanceReportsTab";
        default:
          return "DemandReportTab";
      }
    },
  },
  methods: {
    async fetchBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response;
      } catch (error) {
        console.error('Error fetching blood groups:', error);
      }
    },

    async fetchBloodProducts() {
      try {
        const response = await api.getUnitProducts();
        this.bloodProducts = response;
      } catch (error) {
        console.error('Error fetching blood products:', error);
      }
    },

    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    applyFilters() {
      console.log('Applying filters:', this.filters);
      this.applyFiltersToCurrentTab();
      this.isDropdownOpen = false;
    },

    applyFiltersToCurrentTab() {
      if (this.$refs.currentTabComponent && typeof this.$refs.currentTabComponent.applyFilters === 'function') {
        this.$refs.currentTabComponent.applyFilters(this.filters);
      } else {
        console.warn('applyFilters method not found on current tab component');
      }
    },

    handleDownloadBloodUnits() {
      if (this.$refs.currentTabComponent?.handleDownloadBloodUnits) {
        this.$refs.currentTabComponent.handleDownloadBloodUnits();
      }
    },

    handleDownloadIssuedUnits() {
      if (this.$refs.currentTabComponent?.handleDownloadIssuedUnits) {
        this.$refs.currentTabComponent.handleDownloadIssuedUnits();
      }
    },

    handleDownloadExternalIssuedUnits() {
      if (this.$refs.currentTabComponent?.handleDownloadExternalIssuedUnits) {
        this.$refs.currentTabComponent.handleDownloadExternalIssuedUnits();
      }
    },

    closeDropdown() {
      this.isDropdownOpen = false;
    },

    cancelFilters() {
      this.filters = {
        blood_group: "",
        status: "",
        unit_product: "",
        start_date: "",
        end_date: ""
      };
      this.searchQuery = "";
      this.applyFiltersToCurrentTab();
      this.isDropdownOpen = false;
    },
  },
  mounted() {
    this.user = this.$store.getters["auth/user"];
  }
};
</script>

<style lang="css" scoped></style>